import React, { useState } from 'react';
import { Button } from '../../ui/common/Button';
import type { ThemeMode } from '../../domain/types';
import {
  BACKGROUND_PRESETS,
  WORKSPACE_SECTIONS,
  type BackgroundPreset,
  type BackgroundPresetId,
  type WorkspaceSectionId,
} from '../../domain/backgroundConfig';
import { useTheme } from '../../theme';
import { hintStyle, inputStyle, labelStyle, sectionStyle } from './settingsStyles';

export interface SettingsAppearanceSectionProps {
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
}

export const SettingsAppearanceSection: React.FC<SettingsAppearanceSectionProps> = ({
  theme,
  onThemeChange,
}) => {
  const {
    backgrounds,
    customPresets,
    setSectionBackground,
    setGlobalBackground,
    addCustomImageBackground,
    removeCustomBackground,
    resetBackgrounds,
  } = useTheme();

  const [selectedPresetId, setSelectedPresetId] = useState<BackgroundPresetId>('default');
  const [showAddImage, setShowAddImage] = useState(false);
  const [customImageName, setCustomImageName] = useState('');
  const [customImageUrl, setCustomImageUrl] = useState('');

  const allPresets: BackgroundPreset[] = [...BACKGROUND_PRESETS, ...customPresets];
  const activePreset = allPresets.find((p) => p.id === selectedPresetId) || allPresets[0];

  // Helper to find which sections currently use a given preset
  const getAssignedSections = (presetId: string): WorkspaceSectionId[] => {
    return WORKSPACE_SECTIONS.filter((sec) => backgrounds[sec.id] === presetId).map((sec) => sec.id);
  };

  const handleToggleSectionForPreset = (secId: WorkspaceSectionId, presetId: BackgroundPresetId) => {
    const isCurrentlyAssigned = backgrounds[secId] === presetId;
    if (isCurrentlyAssigned) {
      // If toggling off, fall back to default or none
      setSectionBackground(secId, presetId === 'default' ? 'none' : 'default');
    } else {
      setSectionBackground(secId, presetId);
    }
  };

  const handleApplyToAll = (presetId: BackgroundPresetId) => {
    setGlobalBackground(presetId);
  };

  const handleClearFromAll = (presetId: BackgroundPresetId) => {
    const assigned = getAssignedSections(presetId);
    for (const sec of assigned) {
      setSectionBackground(sec, 'none');
    }
  };

  const handleAddCustomImage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customImageUrl.trim()) return;
    addCustomImageBackground(
      customImageName.trim() || 'Fondo con Imagen',
      customImageUrl.trim(),
      0.4
    );
    setCustomImageName('');
    setCustomImageUrl('');
    setShowAddImage(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size limit (1.5MB max data URL)
    if (file.size > 1.5 * 1024 * 1024) {
      alert('La imagen no debe superar 1.5 MB para almacenamiento local.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const dataUrl = uploadEvent.target?.result as string;
      if (dataUrl) {
        addCustomImageBackground(
          file.name.replace(/\.[^/.]+$/, '') || 'Imagen subida',
          dataUrl,
          0.4
        );
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div data-testid="settings-appearance-section" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* 1. Theme Mode */}
      <section style={sectionStyle}>
        <div style={labelStyle}>Tema del Sistema</div>
        <p style={hintStyle}>Elige el esquema de colores de la interfaz. Se guarda en este dispositivo.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="sm"
            variant={theme === 'light' ? 'primary' : 'ghost'}
            data-testid="settings-theme-light"
            onClick={() => onThemeChange('light')}
          >
            Claro
          </Button>
          <Button
            size="sm"
            variant={theme === 'dark' ? 'primary' : 'ghost'}
            data-testid="settings-theme-dark"
            onClick={() => onThemeChange('dark')}
          >
            Oscuro
          </Button>
        </div>
      </section>

      {/* 2. Workspace Backgrounds Gallery & Screen Assignment Matrix */}
      <section style={{ ...sectionStyle, borderBottom: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={labelStyle}>Galería de Fondos y Asignación por Pantalla</div>
            <p style={hintStyle}>
              Haz clic en un fondo para asignarlo o quitarlo de varias pantallas simultáneamente.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="sm"
              variant={showAddImage ? 'primary' : 'ghost'}
              data-testid="settings-add-image-toggle"
              onClick={() => setShowAddImage((prev) => !prev)}
              style={{ fontSize: 11.5 }}
            >
              {showAddImage ? 'Cerrar agregar' : '+ Agregar imagen'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="settings-background-reset"
              onClick={resetBackgrounds}
              style={{ fontSize: 11.5 }}
            >
              Restablecer
            </Button>
          </div>
        </div>

        {/* Add Custom Image Drawer */}
        {showAddImage && (
          <div
            style={{
              padding: 14,
              borderRadius: 10,
              background: 'var(--z-fill)',
              border: '1px solid var(--z-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--z-text)' }}>
              Agregar nuevo fondo con imagen
            </div>
            <form onSubmit={handleAddCustomImage} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Nombre (ej. Mi wallpaper favorito)"
                  value={customImageName}
                  onChange={(e) => setCustomImageName(e.target.value)}
                  style={{ ...inputStyle, flex: '1 1 200px' }}
                />
                <input
                  type="url"
                  placeholder="URL de la imagen (https://...)"
                  value={customImageUrl}
                  onChange={(e) => setCustomImageUrl(e.target.value)}
                  style={{ ...inputStyle, flex: '2 1 280px' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--z-dim)' }}>
                  <span>Subir desde dispositivo:</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ fontSize: 11.5 }}
                  />
                </label>

                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    size="sm"
                    variant="primary"
                    type="submit"
                    disabled={!customImageUrl.trim()}
                  >
                    Guardar imagen
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Selected Preset Assignment Matrix Bar */}
        <div
          data-testid="bg-assignment-matrix"
          style={{
            padding: 14,
            borderRadius: 12,
            background: 'var(--z-surface)',
            border: '1px solid var(--z-accent-border, var(--z-border))',
            boxShadow: 'var(--z-sh-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--z-text)' }}>
                Fondo seleccionado: {activePreset.name}
              </span>
              {activePreset.isCustom && (
                <button
                  type="button"
                  title="Eliminar este fondo personalizado"
                  onClick={() => removeCustomBackground(activePreset.id)}
                  style={{
                    fontSize: 11,
                    color: 'var(--z-bad)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Eliminar fondo
                </button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                data-testid="bg-apply-all-btn"
                onClick={() => handleApplyToAll(activePreset.id)}
                style={{
                  fontSize: 11.5,
                  padding: '3px 10px',
                  borderRadius: 6,
                  background: 'var(--z-fill)',
                  border: '1px solid var(--z-border)',
                  color: 'var(--z-text-2)',
                  cursor: 'pointer',
                }}
              >
                Aplicar a todas
              </button>
              <button
                type="button"
                data-testid="bg-clear-all-btn"
                onClick={() => handleClearFromAll(activePreset.id)}
                style={{
                  fontSize: 11.5,
                  padding: '3px 10px',
                  borderRadius: 6,
                  background: 'var(--z-fill)',
                  border: '1px solid var(--z-border)',
                  color: 'var(--z-dim)',
                  cursor: 'pointer',
                }}
              >
                Quitar de todas
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--z-dim)', marginRight: 4 }}>
              Aplica en:
            </span>
            {WORKSPACE_SECTIONS.map((sec) => {
              const isChecked = backgrounds[sec.id] === activePreset.id;
              return (
                <button
                  key={sec.id}
                  type="button"
                  data-testid={`bg-screen-toggle-${sec.id}`}
                  onClick={() => handleToggleSectionForPreset(sec.id, activePreset.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: isChecked ? 700 : 500,
                    background: isChecked ? 'var(--z-accent-soft, #EEF2FF)' : 'var(--z-fill)',
                    color: isChecked ? 'var(--z-accent-ink, #3730A3)' : 'var(--z-muted)',
                    border: isChecked
                      ? '1px solid var(--z-accent-border, #C7D2FE)'
                      : '1px solid var(--z-border-2)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isChecked ? 'var(--z-accent)' : 'var(--z-surface)',
                      color: '#FFF',
                      fontSize: 10,
                      fontWeight: 800,
                    }}
                  >
                    {isChecked ? '✓' : ''}
                  </span>
                  <span>{sec.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Preset Cards Grid */}
        <div
          data-testid="bg-presets-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
            marginTop: 6,
          }}
        >
          {allPresets.map((preset) => {
            const isSelected = activePreset.id === preset.id;
            const assignedList = getAssignedSections(preset.id);
            const previewStyle = theme === 'dark' ? preset.darkStyle : preset.lightStyle;

            return (
              <div
                key={preset.id}
                data-testid={`bg-preset-${preset.id}`}
                onClick={() => setSelectedPresetId(preset.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 12,
                  border: isSelected
                    ? '2px solid var(--z-accent)'
                    : '1px solid var(--z-border)',
                  background: 'var(--z-surface)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  textAlign: 'left',
                  boxShadow: isSelected ? '0 0 0 1px var(--z-accent)' : 'var(--z-sh-4)',
                  transform: isSelected ? 'scale(1.01)' : 'none',
                  transition: 'all 0.18s ease',
                }}
              >
                {/* Visual Preview Box */}
                <div
                  style={{
                    height: 72,
                    width: '100%',
                    borderBottom: '1px solid var(--z-border-2)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: 6,
                    boxSizing: 'border-box',
                    ...previewStyle,
                  }}
                >
                  {preset.type === 'image' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'rgba(15, 23, 42, 0.75)',
                        color: '#FFFFFF',
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      Imagen
                    </span>
                  )}
                  {isSelected && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'var(--z-surface)',
                        color: 'var(--z-accent)',
                        boxShadow: 'var(--z-sh-6)',
                      }}
                    >
                      Seleccionado
                    </span>
                  )}
                </div>

                <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--z-text)' }}>
                      {preset.name}
                    </span>
                    {preset.isCustom && (
                      <button
                        type="button"
                        data-testid={`delete-custom-bg-${preset.id}`}
                        title="Eliminar imagen personalizada"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm(`¿Eliminar fondo personalizado "${preset.name}"?`)) {
                            removeCustomBackground(preset.id);
                          }
                        }}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--z-bad)',
                          background: 'var(--z-fill)',
                          border: '1px solid var(--z-border)',
                          borderRadius: 4,
                          padding: '1px 6px',
                          cursor: 'pointer',
                        }}
                      >
                        Eliminar
                      </button>
                    )}
                  </div>

                  <span style={{ fontSize: 11, color: 'var(--z-dim)', lineHeight: 1.3 }}>
                    {preset.description}
                  </span>

                  {/* Active Section Badges */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {assignedList.length > 0 ? (
                      assignedList.map((sec) => (
                        <span
                          key={sec}
                          style={{
                            fontSize: 9.5,
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: 'var(--z-fill-2)',
                            color: 'var(--z-text-2)',
                          }}
                        >
                          {WORKSPACE_SECTIONS.find((s) => s.id === sec)?.label || sec}
                        </span>
                      ))
                    ) : (
                      <span style={{ fontSize: 10, color: 'var(--z-dim)', fontStyle: 'italic' }}>
                        Sin pantallas asignadas
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
